// @flow

import { get, set } from 'lodash';

type fetchMoreType = ({
  variables: Object,
  updateQuery: (
    previousResult: Object,
    result: { fetchMoreResult?: Object }
  ) => Object,
}) => void;

type AfterConnectionProps = {
  data: Object,
  afterPath: Array<string>,
  edgesPath: Array<string>,
  fetchMore: fetchMoreType,
  loading: boolean,
};

type LimitOffsetConnectionInfiniteProps = {
  data: Object,
  edgesPath: Array<string>,
  fetchMore: fetchMoreType,
  loading: boolean,
  offset?: number,
};

type LoadMoreType = () => void | null;

const mergeResults = edgesPath => (previousResult, { fetchMoreResult }) => {
  if (!fetchMoreResult) {
    return previousResult;
  }

  const previousEdges = get(previousResult, edgesPath, []);
  const fetchMoreEdges = get(fetchMoreResult, edgesPath, []);

  // Start with the next result as the base of our object so that
  // properties outside of `edges` are retained. For example, we want
  // the `pageInfo` object (if it exists) to be from the newest result.
  const nextResult = {
    ...fetchMoreResult,
  };

  // Merge the edges together to create a seamless list of results
  set(nextResult, edgesPath, [...previousEdges, ...fetchMoreEdges]);

  return nextResult;
};

const GraphQlFetchMore = {
  afterConnectionPagination: ({
    data,
    afterPath,
    edgesPath,
    fetchMore,
    loading,
  }: AfterConnectionProps): LoadMoreType => () => {
    if (loading) {
      return null;
    }

    return fetchMore({
      variables: {
        after: get(data, afterPath),
      },
      updateQuery: mergeResults(edgesPath),
    });
  },
  limitOffsetConnectionInfinitePagination: ({
    data,
    edgesPath,
    fetchMore,
    loading,
    offset = 0,
  }: LimitOffsetConnectionInfiniteProps): LoadMoreType => {
    const loadMore = () => {
      if (loading) {
        return null;
      }

      return fetchMore({
        variables: {
          offset: get(data, edgesPath).length + offset,
        },
        updateQuery: mergeResults(edgesPath),
      });
    };

    return loadMore;
  },
};

// Intentionally exporting GraphQlFetchMore and not the individual functions to
// force consumers to write code in a self-documenting style, e.g.:
// GraphQlFetchMore.limitOffsetConnectionInfinitePagination()
/** @component */
export default GraphQlFetchMore;
